import {
  BoldItalicUnderlineToggles,
  CreateLink,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { CSSProperties, useEffect, useMemo, useRef } from "react";
import { FieldError } from "react-hook-form";
import { formatHref, formatMarkdownLinksHref } from "ui/utils/formatters";

interface Props {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: FieldError;
  label: string;
  placeholder?: string;
  className?: string;
  rows?: number;
}

export function INControlledEditor({
  value,
  onChange,
  onBlur,
  error,
  label,
  placeholder,
  className,
  rows = 2,
}: Props) {
  const mdxPlugins = useMemo(
    () => [
      headingsPlugin(),
      linkPlugin(),
      listsPlugin(),
      linkDialogPlugin({
        onClickLinkCallback: (url) => {
          window.open(formatHref(url), "_blank", "noopener,noreferrer");
        },
      }),
      toolbarPlugin({
        toolbarContents: () => (
          <>
            <BoldItalicUnderlineToggles />
            <CreateLink />
            <ListsToggle options={["bullet", "number"]} />
          </>
        ),
      }),
    ],
    []
  );
  const editorRef = useRef<MDXEditorMethods>(null);
  const markdown = formatMarkdownLinksHref(value ?? "");
  const lastMarkdownRef = useRef(markdown);
  const editorStyle = {
    "--in-controlled-editor-min-height": `calc(${Math.max(
      rows,
      1
    )} * 1.25rem + 1.5rem)`,
  } as CSSProperties;
  const contentEditableClassName = [
    "in-controlled-editor-content",
    error ? "mdxeditor-content-error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (markdown !== lastMarkdownRef.current) {
      lastMarkdownRef.current = markdown;
      editorRef.current?.setMarkdown(markdown);
    }
  }, [markdown]);

  const handleChange = (newMarkdown: string) => {
    const formattedMarkdown = formatMarkdownLinksHref(newMarkdown);
    lastMarkdownRef.current = newMarkdown;
    onChange(formattedMarkdown);
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <span className="text-sm font-bold text-darkGrey-700">{label}</span>
      <div className="relative mt-2" style={editorStyle}>
        <MDXEditor
          ref={editorRef}
          markdown={markdown}
          plugins={mdxPlugins}
          onChange={handleChange}
          onBlur={() => onBlur?.()}
          contentEditableClassName={contentEditableClassName}
          className={`rounded-xl border bg-white p-3 text-sm font-medium outline-none ${
            error
              ? "border-error text-error"
              : "border-darkGrey-500 text-darkGrey-700"
          }`}
          placeholder={placeholder}
        />
      </div>

      {error && error.message && (
        <p className="mt-1 text-sm font-medium text-error">{error.message}</p>
      )}
    </div>
  );
}
